import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { Flex, InputText, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';

const ADDRESS_LINE_MAX_LENGTH = 512;
const TOWN_OR_CITY_MAX_LENGTH = 256;
const POSTCODE_MAX_LENGTH = 100;

interface AddressFieldNames {
	addressLine1: string;
	addressLine2: string;
	townOrCity: string;
	postcode: string;
}

interface AddressFieldsProps {
	names: AddressFieldNames;
	patientWording?: boolean;
	currentLocation?: boolean;
}

function getTitleKey(patientWording: boolean, currentLocation: boolean): I18nAndL10n.LocaleKey {
	if (currentLocation && patientWording) {
		return 'View.CreateNewMessage.InformationGathering.PatientCurrentLocation.Title';
	}
	if (currentLocation) {
		return 'View.CreateNewMessage.InformationGathering.CurrentLocation.Title';
	}
	if (patientWording) {
		return 'View.CreateNewMessage.InformationGathering.PatientAddress.Title';
	}
	return 'View.CreateNewMessage.InformationGathering.Address.Title';
}

function getDescriptionKey(patientWording: boolean, currentLocation: boolean): I18nAndL10n.LocaleKey | null {
	if (currentLocation) {
		return null;
	}

	return patientWording
		? 'View.CreateNewMessage.InformationGathering.PatientAddress.Description'
		: 'View.CreateNewMessage.InformationGathering.Address.Description';
}

export function AddressFields({ names, patientWording = false, currentLocation = false }: AddressFieldsProps) {
	const titleKey = getTitleKey(patientWording, currentLocation);
	const descriptionKey = getDescriptionKey(patientWording, currentLocation);
	const requiredMessage = I18nAndL10n.translate('Form.ValidationError.Required');

	return (
		<Flex gap={tokens.spacing.x1_5}>
			<Text variant='headingXSmall'>{withTranslation(titleKey)}</Text>
			{descriptionKey === null ? null : <Text variant='bodyRegular'>{withTranslation(descriptionKey)}</Text>}
			<InputText
				autoComplete='address-line1'
				label={withTranslation('View.CreateNewMessage.InformationGathering.Address.Line1')}
				name={names.addressLine1}
				maxLength={ADDRESS_LINE_MAX_LENGTH}
				required
				options={{ required: requiredMessage }}
			/>
			<InputText
				autoComplete='address-line2'
				label={withTranslation('View.CreateNewMessage.InformationGathering.Address.Line2')}
				name={names.addressLine2}
				maxLength={ADDRESS_LINE_MAX_LENGTH}
				optional
			/>
			<InputText
				autoComplete='address-level2'
				label={withTranslation('View.CreateNewMessage.InformationGathering.Address.TownOrCity')}
				name={names.townOrCity}
				maxLength={TOWN_OR_CITY_MAX_LENGTH}
				required
				options={{ required: requiredMessage }}
			/>
			<InputText
				autoComplete='postal-code'
				label={withTranslation('View.CreateNewMessage.InformationGathering.Address.Postcode')}
				name={names.postcode}
				maxLength={POSTCODE_MAX_LENGTH}
				required
				options={{ required: requiredMessage }}
			/>
		</Flex>
	);
}
